import { isFetchBaseQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";

/**
 * ValidationError is the validation error as
 * returned by the backend.
 */
export interface ValidationError {
  message: string;
  errors: FieldErrors;
}

export function fieldErrors(error: unknown, field: string): string[] | null {
  if (!error) {
    return null;
  }

  if (!isValidationError(error)) {
    return null;
  }

  return error.errors[field] ?? null;
}

/**
 * fieldErrorsForPrefix extracts the field errors for with the given prefix.
 * @param error
 * @param prefix
 */
export function fieldErrorsForPrefix(
  error: ValidationError | null,
  prefix: string,
): FieldErrors | undefined {
  if (!error) {
    return undefined;
  }

  if (prefix === "") {
    return error.errors;
  }

  return Object.fromEntries(
    Object.entries(error.errors)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, value]) => [key.slice(prefix.length), value]),
  );
}

export function isValidationError(error: unknown): error is ValidationError {
  // test if error is an object
  if (!error || typeof error !== "object") {
    return false;
  }

  // test if error has the required fields
  if (!("message" in error) || !("errors" in error)) {
    return false;
  }

  // test if the fields are of the correct type
  if (typeof error.message !== "string") {
    return false;
  }

  return isFieldErrors(error.errors);
}

export function rtkErrIsValidationError(
  error: unknown,
): error is { data: ValidationError } {
  if (!error) {
    return false;
  }

  if (!isFetchBaseQueryError(error)) {
    return false;
  }

  if (error.status !== 422) {
    return false;
  }

  return isValidationError(error.data);
}

export interface FieldErrors {
  [field: string]: string[];
}

export function isFieldErrors(errors: unknown): errors is FieldErrors {
  if (typeof errors !== "object" || errors === null) {
    return false;
  }

  return Object.entries(errors).every(
    ([, v]) => Array.isArray(v) && v.every((e) => typeof e === "string"),
  );
}

/**
 * mergeFieldErrors combines the field errors of several sources. Messages of
 * the same field are concatenated, duplicates are dropped.
 */
export function mergeFieldErrors(
  ...sources: (FieldErrors | undefined)[]
): FieldErrors {
  const merged: FieldErrors = {};
  for (const source of sources) {
    for (const [field, messages] of Object.entries(source ?? {})) {
      merged[field] = [...new Set([...(merged[field] ?? []), ...messages])];
    }
  }
  return merged;
}

/**
 * The code of a failed request validation (HTTP 422), see
 * nidavellir/go/tools/validator.ErrValidationFailed.
 */
const errValidationFailedCode =
  "nidavellir.tools.validator.ErrValidationFailed";

/**
 * The code of a document which is not ready for the customer (HTTP 400),
 * see bsm.ErrDocumentNotReady. It has the same shape as a validation error.
 */
const errDocumentNotReadyCode = "bsm.ErrDocumentNotReady";

const validationErrorCodes = [errValidationFailedCode, errDocumentNotReadyCode];

/**
 * validationFieldErrorsFrom returns the field errors of a validation error
 * or a document not ready error. It accepts the error of an RTK Query hook
 * as well as the plain response body. Any other error yields null.
 */
export function validationFieldErrorsFrom(error: unknown): FieldErrors | null {
  const body = isFetchBaseQueryError(error) ? error.data : error;
  if (!body || typeof body !== "object") {
    return null;
  }

  if (!("error" in body) || !("errors" in body)) {
    return null;
  }

  // We rely on the backend to serve the correct codes. This may break
  // the portal if they change, but failing fast is preferred here.
  if (
    typeof body.error !== "string" ||
    !validationErrorCodes.includes(body.error)
  ) {
    return null;
  }

  if (!isFieldErrors(body.errors)) {
    return null;
  }

  return body.errors;
}

/**
 * validationErrorMessages returns the distinct messages of all fields of a
 * validation error or a document not ready error (see
 * {@link validationFieldErrorsFrom}), e.g. to show them in a toast. Any
 * other error yields none. The keys are left out, since they hold ids.
 * The messages are returned as the backend sends them: those of custom
 * rules are German, those of the validator package English.
 */
export function validationErrorMessages(error: unknown): string[] {
  const errors = validationFieldErrorsFrom(error);
  if (!errors) {
    return [];
  }

  return [...new Set(Object.values(errors).flat())];
}
